import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ErrorMessages, Question, Rules } from '../../../config/types';
import { getErrorMessage } from '../../../utils/form-utils';
import Details from '../Details';
import ErrorMessage from '../ErrorMessage';
import MonthInput from './MonthInput';
import YearInput from './YearInput';
import {
  dateIsEmpty,
  dateIsFullyPopulated,
  getMinYearValue,
  invalidDateErrorMessage,
  isMinYearRuleValid,
  isValidDate,
} from './utils';

function dateNowStr(): string {
  // We only want the year and month for comparison
  return new Date().toISOString().slice(0, 7);
}

function normaliseDateString(value: string): string {
  // We want to ensure the value is in the format yyyy-mm, adding leading zeros if needed
  const values = value.split('-');
  return `${values[0].padStart(4, '0')}-${values[1].padStart(2, '0')}`;
}

export function isBeforeNowRuleValid(rules: Rules, value: string) {
  if (rules.isBeforeNow && rules.isBeforeNow === 'enabled' && dateIsFullyPopulated(value)) {
    // Compare the dates using string comparison, no need to convert to Dates
    if (!(normaliseDateString(value) < dateNowStr())) {
      return false;
    }
  }
  return true;
}

export function isAfterNowRuleValid(rules: Rules, value: string): boolean {
  if (rules.isAfterNow && rules.isAfterNow === 'enabled' && dateIsFullyPopulated(value)) {
    // Compare the dates using string comparison, no need to convert to Dates
    if (!(normaliseDateString(value) > dateNowStr())) {
      return false;
    }
  }
  return true;
}

const validateDateInputField = (
  question: Question,
  rules: Rules,
  value: string,
  errorMessages: ErrorMessages,
) => {
  if (rules.required && !dateIsFullyPopulated(value)) {
    return getErrorMessage('required', question.title, '', errorMessages?.required || '');
  }

  if (!dateIsEmpty(value)) {
    if (!dateIsFullyPopulated(value) || !isValidDate(value)) {
      return invalidDateErrorMessage(question);
    }
  }

  if (!isBeforeNowRuleValid(rules, value)) {
    return getErrorMessage('isBeforeNow', question.title, '', errorMessages?.isBeforeNow || '');
  }

  if (!isAfterNowRuleValid(rules, value)) {
    return getErrorMessage('isAfterNow', question.title, '', errorMessages?.isAfterNow || '');
  }

  if (!isMinYearRuleValid(rules, value)) {
    const minYearInt = getMinYearValue(rules);
    return getErrorMessage(
      'minYear',
      question.title,
      `${minYearInt}`,
      errorMessages?.minYear || '',
    );
  }

  return undefined;
};

interface ShortDateInputProps {
  name: string;
  defaultValue?: string; // An ISO date string with only the yyyy-mm
  disabled?: boolean;
  monthLabel?: string;
  yearLabel?: string;
  anchorId?: string;
  onChange?(value: string): void;
}

export function ShortDateInput({
  name,
  defaultValue,
  disabled = false,
  monthLabel = 'Month',
  yearLabel = 'Year',
  anchorId = useId(),
  onChange,
}: ShortDateInputProps) {
  const monthId = useMemo(() => `${anchorId}`, [anchorId]);
  const yearId = useMemo(() => `${anchorId}2`, [anchorId]);

  // The value should be a valid year and month in ISO format e.g. 2024-01
  const values = useMemo<string[]>(() => {
    const initValue = defaultValue ?? '-';
    return initValue.split('-');
  }, [defaultValue]);
  const [monthValue, setMonthValue] = useState(values[1]);
  const [yearValue, setYearValue] = useState(values[0]);

  const triggerChange = (newValue: string) => {
    if (onChange) {
      if (dateIsEmpty(newValue)) {
        onChange('');
      } else {
        onChange(newValue);
      }
    }
  };

  const handleMonthChange = (month: string) => {
    setMonthValue(month);
    triggerChange(`${yearValue}-${month}`);
  };

  const handleYearChange = (year: string) => {
    setYearValue(year);
    triggerChange(`${year}-${monthValue}`);
  };

  return (
    <div className="govuk-date-input">
      <MonthInput
        testId={`${name}-month`}
        disabled={disabled}
        label={monthLabel}
        anchorId={monthId}
        onChange={handleMonthChange}
        value={monthValue}
      />
      <YearInput
        testId={`${name}-year`}
        disabled={disabled}
        label={yearLabel}
        anchorId={yearId}
        onChange={handleYearChange}
        value={yearValue}
      />
    </div>
  );
}

interface ShortDateInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

export function ShortDateInputField({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: ShortDateInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    // If the value is currently not defined in the form, initialise it
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      validateDateInputField(question, question?.rules || {}, value, question.errorMessages || {}),
  });

  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-date-input">
        <ShortDateInput
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
